import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { builtInToolName } from "../../../api/content/processDefinitions";
import { useLoad } from "../../../shared/loading/useLoad";
import { appPaths } from "../../../shared/routing/appPaths";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import Field from "../../../ui/forms/Field";
import { FORM_HELP_TEXT_CLASS, FORM_SELECT_CLASS } from "../../../ui/styles/formStyles";
import HelpArticleLink from "../../help/HelpArticleLink";
import { loadProcess } from "../../workflows/data/processCatalogue";
import { projectProcessReader } from "../../workflows/data/projectProcessReader";
import { importToolsOf, type ImportWorkflowChoice } from "./importSetup";
import ButtonLink from "../../../ui/buttons/ButtonLink";

/**
 * The import Workflow the form runs (F16): which one, the tool it runs by name, and a way
 * to open that tool. Its page shows how it works and offers "Ask the agent" for a new
 * version. Nothing shows while there is none to choose; the reason stands by the button.
 */
export default function ImportWorkflowField({ projectId, choice, pinned = null }: { projectId: string; choice: ImportWorkflowChoice; pinned?: string | null }) {
  const { t } = useTranslation();
  const { loaded, candidates, workflow } = choice;
  if (pinned !== null) {
    // The corpus's uploads run the Workflow its first upload pinned: shown, not chosen.
    return (
      <Field label={t("sources.import.workflow")} hint={t("sources.import.workflowPinned")} actions={<HelpArticleLink article="importCorpus" />}>
        {() => <ImportTools key={pinned} projectId={projectId} workflow={pinned} showName />}
      </Field>
    );
  }
  if (loaded.state.status !== "loaded") return <LoadStatus state={loaded.state} loadingLabel={t("common.loading")} onRetry={loaded.reload} />;
  if (workflow === null) return null;
  return (
    <Field label={t("sources.import.workflow")} hint={t("sources.import.workflowHint")} actions={<HelpArticleLink article="importCorpus" />}>
      {(control) => (
        <div className="space-y-2">
          {candidates.length > 1 ? (
            <select {...control} className={FORM_SELECT_CLASS} value={workflow.reference} onChange={(event) => { choice.choose(event.target.value); }}>
              {candidates.map((candidate) => <option key={candidate.reference} value={candidate.reference}>{candidate.name}</option>)}
            </select>
          ) : (
            <p id={control.id} className="app-title font-medium">{workflow.name}</p>
          )}
          <ImportTools key={workflow.reference} projectId={projectId} workflow={workflow.reference} />
        </div>
      )}
    </Field>
  );
}

function ImportTools({ projectId, workflow, showName = false }: { projectId: string; workflow: string; showName?: boolean }) {
  const { t } = useTranslation();
  const load = useCallback(async (signal: AbortSignal) => {
    const loaded = await loadProcess(workflow, projectProcessReader(projectId, signal));
    return { name: loaded.workflow.name, tools: importToolsOf(loaded.workflow, loaded.catalogue) };
  }, [projectId, workflow]);
  const { state, reload } = useLoad(load);
  if (state.status !== "loaded") return <LoadStatus state={state} loadingLabel={t("common.loading")} onRetry={reload} />;
  return (
    <ul className="space-y-1">
      {showName ? <li className="app-title font-medium">{state.value.name}</li> : null}
      {state.value.tools.map((tool) => (
        <li key={tool.reference} className="flex flex-wrap items-center gap-2">
          <span className={FORM_HELP_TEXT_CLASS}>{t("sources.import.tool", { name: tool.name })}</span>
          {builtInToolName(tool.reference) !== null ? null : (
            <ButtonLink to={appPaths.item(tool.reference)} target="_blank" rel="noreferrer" size="xs" title={t("sources.import.openToolHint")}>
              {t("sources.import.openTool")}
            </ButtonLink>
          )}
        </li>
      ))}
      <li>
        <Link to={appPaths.workflow(workflow)} target="_blank" rel="noreferrer" className="app-link text-sm underline">{t("sources.import.openWorkflow")}</Link>
      </li>
    </ul>
  );
}
